import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { ReportTabsData } from '@/features/reports/types/reports';
import { useInfiniteReportProgressQuery } from '@/features/reports/api/useReportsQueries';
import { Empty, FilterDropdown, KpiCard, Loading } from '../ReportDetailPagePrimitives';

const PAGE_SIZE = 48;

function getTimingCardStyle(status?: string) {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED' || s === 'PLAYED') {
    return {
      cardClass: 'border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50/80 hover:shadow-sm',
      statusClass: 'text-emerald-600',
      label: s === 'SUCCESS' ? 'APPROVED' : s,
    };
  }
  if (
    s === 'FAILED' ||
    s === 'CANCEL' ||
    s === 'CANCELLED' ||
    s === 'EXPIRED' ||
    s === 'REJECTED'
  ) {
    return {
      cardClass: 'border-red-300 bg-red-50/50 hover:bg-red-50/80 hover:shadow-sm',
      statusClass: 'text-red-600',
      label: s === 'FAILED' ? 'FAILED' : s,
    };
  }
  // Default to Pending / Amber Yellowish
  return {
    cardClass: 'border-amber-300 bg-amber-50/50 hover:bg-amber-50/80 hover:shadow-sm',
    statusClass: 'text-amber-600',
    label: s || 'PENDING',
  };
}

export function ProgressTab({
  token,
  data: initialData,
}: {
  token?: string;
  data?: ReportTabsData;
}) {
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteReportProgressQuery(
    token,
    {
      date: date || undefined,
      slot: slot || undefined,
    },
    PAGE_SIZE,
  );


  const firstPageData = query.data?.pages?.[0]?.data || initialData;
  const uniqueDates = firstPageData?.uniqueDates || [];
  const sortedSlots = firstPageData?.sortedSlots || [];

  console.log("query", firstPageData)
  const playTimings = useMemo(() => {
    if (query.data?.pages?.length) {
      return query.data.pages.flatMap((p) => p.data?.playTimings || []);
    }
    return initialData?.playTimings || [];
  }, [query.data?.pages, initialData?.playTimings]);

  // Reset scroll position when filter changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [date, slot]);


  const hasNextPage = query.hasNextPage;
  const isFetchingNextPage = query.isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void query.fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, query]);

  // Container scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const threshold = 120;
      const isNearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <= threshold;

      if (isNearBottom) {
        loadMore();
      }
    },
    [loadMore],
  );

  // IntersectionObserver targeting the scrollable container root
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: container,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, loadMore]);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <KpiCard
          label="Total"
          value={firstPageData?.totalRepetitions || 0}
          color="text-[var(--dash-ink)]"
          bg="bg-[var(--dash-soft)]"
        />
        <KpiCard
          label="Approved"
          value={firstPageData?.successRepetitions ?? 0}
          color="text-emerald-700"
          bg="bg-emerald-50"
        />
        <KpiCard
          label="Pending"
          value={firstPageData?.pendingRepetitions ?? 0}
          color="text-amber-700"
          bg="bg-amber-50"
        />
        <KpiCard
          label="Failed"
          value={firstPageData?.failedRepetitions ?? 0}
          color="text-red-700"
          bg="bg-red-50"
        />
        <KpiCard
          label="Rate"
          value={`${firstPageData?.successRate != null ? Number(firstPageData.successRate).toFixed(1) : '0.0'}%`}
          color="text-[var(--dash-brand)]"
          bg="bg-blue-50"
        />
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-[var(--dash-line)] bg-white px-4 py-3">
        {uniqueDates.length ? (
          <FilterDropdown
            label="Date"
            value={date}
            onChange={setDate}
            allLabel="All Dates"
            options={uniqueDates.map((d) => ({ value: d, label: d }))}
          />
        ) : null}
        {sortedSlots.length ? (
          <FilterDropdown
            label="Time Slot"
            value={slot}
            onChange={setSlot}
            allLabel="All Time Slots"
            options={sortedSlots.map((s) => ({
              value: String(s.hour ?? ''),
              label: s.formattedSlot || String(s.hour ?? ''),
            }))}
          />
        ) : null}
      </div>

      {query.isPending && !playTimings.length ? (
        <Loading />
      ) : playTimings.length === 0 ? (
        <Empty text="Progress will appear once the campaign starts playing." />
      ) : (
        <div className="flex flex-col gap-3 rounded-2xl border border-[var(--dash-line)] bg-white p-3 sm:p-4">
          <div className="flex items-center justify-between px-1 text-xs text-[var(--dash-muted)]">
            <span className="font-medium">
              Showing {playTimings.length} of {firstPageData?.totalRepetitions || 0} timings
            </span>
            {hasNextPage && (
              <span className="text-[11px] font-semibold text-[var(--dash-brand)]">
                Scroll down to load more
              </span>
            )}
          </div>

          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="grid max-h-[540px] grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
          >
            {playTimings.map((t, i) => {
              const { cardClass, statusClass, label } = getTimingCardStyle(t.status);
              const displayTime =
                t.formattedTime ||
                (t.date && t.time ? `${t.date} ${t.time}` : '—');

              return (
                <div
                  key={i}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2.5 py-3.5 text-center transition hover:-translate-y-0.5 ${cardClass}`}
                >
                  <span className="text-[11px] font-semibold leading-relaxed text-slate-800">
                    {displayTime}
                  </span>
                  <span
                    className={`text-[11px] font-bold tracking-wider uppercase ${statusClass}`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}

            {hasNextPage && (
              <div
                ref={sentinelRef}
                className="col-span-full flex flex-col items-center justify-center py-4 text-center"
              >
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={loadMore}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-4 py-2 text-xs font-semibold text-[var(--dash-ink)] shadow-sm transition hover:border-[var(--dash-brand)] hover:bg-white hover:text-[var(--dash-brand)] disabled:opacity-50"
                >
                  <Loader2
                    className={`h-3.5 w-3.5 text-[var(--dash-brand)] ${isFetchingNextPage ? 'animate-spin' : ''
                      }`}
                  />
                  <span>
                    {isFetchingNextPage ? 'Loading more...' : 'Load more'}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}




